<template>
  <div class="special-rate-approval-view pa-4">
    <!-- 1. Header / Tarjeta de Título Neutra -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center py-4 px-5 flex-wrap">
        <v-avatar color="grey lighten-4" size="42" class="mr-3">
          <v-icon color="grey darken-3">mdi-check-decagram-outline</v-icon>
        </v-avatar>
        <div>
          <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
            Aprobación de Tarifas de Menú Especial
          </h2>
          <span class="text-caption grey--text text--darken-2">
            Bandeja de autorización previa para tarifas y menús de eventos especiales en comedor.
          </span>
        </div>
      </v-card-title>
    </v-card>

    <!-- 2. Tarjeta Filtros de Búsqueda -->
    <v-card class="mb-5 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="py-3 px-5 d-flex align-center flex-wrap">
        <v-icon color="grey darken-3" small class="mr-2">mdi-filter-variant</v-icon>
        <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
          Filtros de Búsqueda
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-4 px-5">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="4">
            <s-date label="Fecha Desde" v-model="filters.BeginDate" clearable />
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <s-date label="Fecha Hasta" v-model="filters.EndDate" clearable />
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <!-- DEFINICIÓN 1761 CON CLEARABLE (NULL = TODOS) -->
            <s-select-definition
              :def="1761"
              label="Estado de Aprobación"
              v-model="filters.ApprovalStatus"
              clearable
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 3. Contenedor de Tabla de Datos -->
    <v-card class="rounded-xl border-light bg-white pa-4 mb-5" outlined elevation="0">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        item-key="MlrID"
        dense
        class="border-rounded elevation-0"
        :items-per-page="15"
        no-data-text="No se encontraron tarifas especiales con los filtros seleccionados"
      >
        <!-- Motivo / Menú -->
        <template v-slot:[`item.MlrReason`]="{ item }">
          <div class="py-1">
            <span class="font-weight-bold grey--text text--darken-4">
              {{ item.MlrReason || item.MlrDescription || 'Menú Especial' }}
            </span>
            <div class="caption grey--text text--darken-1" v-if="item.TypeMenuText">
              {{ item.TypeMenuText }}
            </div>
          </div>
        </template>

        <!-- Válido Desde -->
        <template v-slot:[`item.BeginDate`]="{ item }">
          <span class="font-weight-medium">{{ formatDate(item.BeginDate) }}</span>
        </template>

        <!-- Válido Hasta -->
        <template v-slot:[`item.EndDate`]="{ item }">
          <span class="font-weight-medium">{{ formatDate(item.EndDate) }}</span>
        </template>

        <!-- Precio Regular -->
        <template v-slot:[`item.MlrPrice`]="{ item }">
          <span class="font-weight-bold primary--text">
            S/ {{ Number(item.MlrPrice != null ? item.MlrPrice : (item.MlrPriceWorkerRegular || 0)).toFixed(2) }}
          </span>
        </template>

        <!-- Precio Descuento -->
        <template v-slot:[`item.MrlDiscount`]="{ item }">
          <span class="font-weight-medium grey--text text--darken-3">
            S/ {{ Number(item.MrlDiscount != null ? item.MrlDiscount : (item.MlrPriceWorkerDiscount || 0)).toFixed(2) }}
          </span>
        </template>

        <!-- Estado -->
        <template v-slot:[`item.MlrApprovalStatus`]="{ item }">
          <v-chip
            small
            label
            :color="getStatusColor(item.MlrApprovalStatus)"
            text-color="white"
            class="font-weight-bold"
          >
            <v-icon left x-small>{{ getStatusIcon(item.MlrApprovalStatus) }}</v-icon>
            {{ item.ApprovalStatusText || getStatusText(item.MlrApprovalStatus) }}
          </v-chip>
        </template>

        <!-- Alcance -->
        <template v-slot:[`item.ScopeText`]="{ item }">
          <v-chip
            small
            outlined
            :color="item.MlrScope === 2 ? 'indigo darken-2' : 'teal darken-2'"
            class="font-weight-bold"
          >
            <v-icon left x-small>{{ item.MlrScope === 2 ? 'mdi-account-check' : 'mdi-account-group' }}</v-icon>
            {{ item.ScopeText || (item.MlrScope === 2 ? 'SELECTIVO' : 'GENERAL') }}
          </v-chip>
        </template>

        <!-- Solicitante -->
        <template v-slot:[`item.UsrCreateName`]="{ item }">
          <span class="caption grey--text text--darken-3">{{ item.UsrCreateName || '-' }}</span>
        </template>

        <!-- Aprobador -->
        <template v-slot:[`item.UsrApprovalName`]="{ item }">
          <span class="caption grey--text text--darken-3">{{ item.UsrApprovalName || '-' }}</span>
        </template>

        <!-- Fecha Aprobación -->
        <template v-slot:[`item.MlrApprovalDate`]="{ item }">
          <span class="caption">{{ formatDateTime(item.MlrApprovalDate) }}</span>
        </template>

        <!-- Observación -->
        <template v-slot:[`item.MlrApprovalComment`]="{ item }">
          <span class="caption font-italic grey--text text--darken-2" v-if="item.MlrApprovalComment">
            "{{ item.MlrApprovalComment }}"
          </span>
          <span v-else class="grey--text text--lighten-1 caption">-</span>
        </template>

        <!-- Acciones -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex align-center justify-center" v-if="item.MlrApprovalStatus === 1">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="action-btn-approve rounded-lg mr-2"
                  v-bind="attrs"
                  v-on="on"
                  @click="promptDecision(item, 2)"
                >
                  <v-icon small color="green darken-2">mdi-thumb-up</v-icon>
                </v-btn>
              </template>
              <span>Aprobar Tarifa (Pulgar arriba)</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="action-btn-reject rounded-lg"
                  v-bind="attrs"
                  v-on="on"
                  @click="openRejectDialog(item)"
                >
                  <v-icon small color="red darken-2">mdi-thumb-down</v-icon>
                </v-btn>
              </template>
              <span>Rechazar Tarifa (Pulgar abajo)</span>
            </v-tooltip>
          </div>
          <div v-else class="d-flex align-center justify-center">
            <v-tooltip bottom v-if="item.MlrApprovalStatus === 2">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small color="green darken-1" v-bind="attrs" v-on="on">mdi-thumb-up-outline</v-icon>
              </template>
              <span>Tarifa Aprobada</span>
            </v-tooltip>
            <v-tooltip bottom v-else-if="item.MlrApprovalStatus === 3">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small color="red darken-1" v-bind="attrs" v-on="on">mdi-thumb-down-outline</v-icon>
              </template>
              <span>Tarifa Rechazada</span>
            </v-tooltip>
            <span v-else class="grey--text text--lighten-1 caption">-</span>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Diálogo para comentario de Rechazo -->
    <v-dialog v-model="rejectDialog" max-width="500px" persistent>
      <v-card outlined class="rounded-xl border-light">
        <v-card-title class="py-3 px-5 d-flex align-center">
          <v-avatar color="red lighten-5" size="36" class="mr-3">
            <v-icon color="error" small>mdi-thumb-down</v-icon>
          </v-avatar>
          <div>
            <div class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
              Rechazar Tarifa Especial
            </div>
          </div>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4 pb-2 px-5">
          <p class="subtitle-2 grey--text text--darken-2 mb-3">
            Ingrese el motivo o justificación del rechazo para:
            <br />
            <strong class="grey--text text--darken-4">{{ selectedItem ? (selectedItem.MlrReason || selectedItem.MlrDescription) : '' }}</strong>
          </p>
          <s-textarea
            v-model="rejectComment"
            label="Motivo del rechazo *"
            rows="3"
            counter="250"
          ></s-textarea>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="px-5 py-3">
          <v-spacer></v-spacer>
          <v-btn
            outlined
            color="grey darken-2"
            class="rounded-lg font-weight-bold text-none"
            @click="rejectDialog = false"
            :disabled="processingDecision"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            elevation="0"
            class="rounded-lg font-weight-bold text-none ml-2"
            @click="confirmReject"
            :loading="processingDecision"
          >
            Confirmar Rechazo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import MealRateService from "@/services/DinningHall/MealRateService";

export default {
  name: "SpecialMealRateApproval",
  data() {
    return {
      loading: false,
      processingDecision: false,
      rejectDialog: false,
      rejectComment: "",
      selectedItem: null,
      filters: {
        BeginDate: null,
        EndDate: null,
        ApprovalStatus: null // null para filtrar todos
      },
      headers: [
        { text: "Acciones", value: "actions", sortable: false, width: "110px", align: "center" },
        { text: "Estado", value: "MlrApprovalStatus", sortable: true, width: "125px", align: "center" },
        { text: "Alcance", value: "ScopeText", sortable: true, width: "130px", align: "center" },
        { text: "Motivo / Menú", value: "MlrReason", sortable: true },
        { text: "Válido Desde", value: "BeginDate", sortable: true, width: "115px" },
        { text: "Válido Hasta", value: "EndDate", sortable: true, width: "115px" },
        { text: "P. Regular", value: "MlrPrice", sortable: false, width: "100px", align: "right" },
        { text: "P. Descuento", value: "MrlDiscount", sortable: false, width: "110px", align: "right" },
        { text: "Solicitante", value: "UsrCreateName", sortable: true, width: "180px" },
        { text: "Aprobador", value: "UsrApprovalName", sortable: true, width: "180px" },
        { text: "Fec. Aprob/Rech", value: "MlrApprovalDate", sortable: true, width: "135px" },
        { text: "Observación", value: "MlrApprovalComment", sortable: false }
      ],
      items: []
    };
  },
  watch: {
    "filters.BeginDate"() {
      this.loadData();
    },
    "filters.EndDate"() {
      this.loadData();
    },
    "filters.ApprovalStatus"() {
      this.loadData();
    }
  },
  created() {
    this.setDefaultDates();
    this.loadData();
  },
  methods: {
    setDefaultDates() {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      this.filters.BeginDate = this.formatDateIso(firstDay);
      this.filters.EndDate = this.formatDateIso(lastDay);
    },
    formatDateIso(date) {
      const d = new Date(date);
      const month = "" + (d.getMonth() + 1);
      const day = "" + d.getDate();
      const year = d.getFullYear();
      return [year, month.padStart(2, "0"), day.padStart(2, "0")].join("-");
    },
    formatDate(val) {
      if (!val) return "-";
      return val.substring(0, 10);
    },
    formatDateTime(val) {
      if (!val) return "-";
      return val.replace("T", " ").substring(0, 16);
    },
    getStatusText(status) {
      switch (status) {
        case 1:
          return "PENDIENTE";
        case 2:
          return "APROBADO";
        case 3:
          return "RECHAZADO";
        default:
          return "DESCONOCIDO";
      }
    },
    getStatusIcon(status) {
      switch (status) {
        case 1:
          return "mdi-clock-outline";
        case 2:
          return "mdi-check";
        case 3:
          return "mdi-close";
        default:
          return "mdi-help";
      }
    },
    getStatusColor(status) {
      switch (status) {
        case 1:
          return "orange darken-2";
        case 2:
          return "green darken-1";
        case 3:
          return "red darken-1";
        default:
          return "grey darken-1";
      }
    },
    loadData() {
      this.loading = true;
      const requestPayload = {
        length: 500,
        start: 0,
        data: {
          BeginDate: this.filters.BeginDate,
          EndDate: this.filters.EndDate,
          MlrApprovalStatus: this.filters.ApprovalStatus || 0
        }
      };

      MealRateService.approvalList(requestPayload)
        .then(response => {
          this.items = (response && response.data && response.data.data) ? response.data.data : [];
        })
        .catch(error => {
          const msg = error?.response?.data?.Message || "Error al cargar las tarifas especiales";
          this.$fun.alert(msg, "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    promptDecision(item, status) {
      const actionName = status === 2 ? "aprobar" : "rechazar";
      const confirmQuestion = `¿Está seguro de ${actionName} la tarifa especial "${item.MlrReason || item.MlrDescription}"?`;

      this.$fun.alert(confirmQuestion, "question").then(result => {
        if (result && (result.value || result.isConfirmed)) {
          this.executeDecision(item, status, null);
        }
      });
    },
    openRejectDialog(item) {
      this.selectedItem = item;
      this.rejectComment = "";
      this.rejectDialog = true;
    },
    confirmReject() {
      if (!this.rejectComment || this.rejectComment.trim().length === 0) {
        this.$fun.alert("Debe ingresar un motivo para rechazar la tarifa especial.", "warning");
        return;
      }
      this.executeDecision(this.selectedItem, 3, this.rejectComment.trim());
    },
    executeDecision(item, status, comment) {
      this.processingDecision = true;
      const usrID = this.$fun.getUserID();
      const payload = {
        MlrID: item.MlrID,
        MlrApprovalStatus: status,
        MlrApprovalComment: comment
      };

      MealRateService.approve(payload, usrID)
        .then(response => {
          const successMsg = status === 2 ? "Tarifa especial aprobada con éxito." : "Tarifa especial rechazada.";
          this.$fun.alert(successMsg, "success");
          this.rejectDialog = false;
          this.loadData();
        })
        .catch(error => {
          const msg = error?.response?.data?.Message || "Ocurrió un error al procesar la solicitud.";
          this.$fun.alert(msg, "error");
        })
        .finally(() => {
          this.processingDecision = false;
        });
    }
  }
};
</script>

<style scoped>
.border-light {
  border: 1px solid #e2e8f0 !important;
}
.border-rounded {
  border-radius: 12px;
}
.action-btn-approve {
  background-color: #f0fdf4 !important;
  border: 1px solid #bbf7d0 !important;
  transition: all 0.2s ease-in-out;
}
.action-btn-approve:hover {
  background-color: #dcfce7 !important;
  border-color: #86efac !important;
  transform: translateY(-1px);
}
.action-btn-reject {
  background-color: #fef2f2 !important;
  border: 1px solid #fecaca !important;
  transition: all 0.2s ease-in-out;
}
.action-btn-reject:hover {
  background-color: #fee2e2 !important;
  border-color: #fca5a5 !important;
  transform: translateY(-1px);
}
</style>
